/**
 * Last Known Good Store
 * Persists the last successfully rendered flight data for fallback display
 * Uses localStorage for persistence across reloads
 */

const STORAGE_KEY_PREFIX = 'fids_last_known_good_';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes max age for cached data

/**
 * Store flight data as last-known-good
 * @param {string} boardType - 'arrivals' or 'departures'
 * @param {Array} flights - Normalized flight data array
 */
export function storeLastKnownGood(boardType, flights) {
  if (!flights || !Array.isArray(flights) || flights.length === 0) {
    return; // Don't store empty data
  }

  const data = {
    flights: flights.map(f => ({
      // Store only display-relevant fields to minimize storage
      key: f.key,
      flight: f.flight,
      airlineCode: f.airlineCode,
      airlineName: f.airlineName,
      scheduledTime: f.scheduledTime,
      estimatedTime: f.estimatedTime,
      actualTime: f.actualTime,
      status: f.status,
      displayname: f.displayname,
      terminal: f.terminal,
      gate: f.gate,
      baggage: f.baggage,
      belt: f.belt,
      codeSharesAsCodes: f.codeSharesAsCodes
    })),
    timestamp: Date.now(),
    count: flights.length
  };

  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${boardType}`,
      JSON.stringify(data)
    );
    console.log(`[LastKnownGood] Stored ${flights.length} ${boardType} flights`);
  } catch (err) {
    console.warn('[LastKnownGood] Failed to store data:', err.message);
    // Try to clear old data if storage is full
    try {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${boardType}`);
    } catch (e) {
      // Ignore
    }
  }
}

/**
 * Retrieve last-known-good flight data
 * @param {string} boardType - 'arrivals' or 'departures'
 * @returns {{ flights: Array, timestamp: number, age: number, isStale: boolean } | null}
 */
export function getLastKnownGood(boardType) {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${boardType}`);
    if (!raw) return null;

    const data = JSON.parse(raw);
    const age = Date.now() - data.timestamp;
    const isStale = age > MAX_AGE_MS;

    return {
      flights: data.flights || [],
      timestamp: data.timestamp,
      formattedTime: formatTimestamp(data.timestamp),
      age,
      ageSeconds: Math.floor(age / 1000),
      isStale,
      count: data.count || data.flights?.length || 0
    };
  } catch (err) {
    console.warn('[LastKnownGood] Failed to retrieve data:', err.message);
    return null;
  }
}

/**
 * Clear last-known-good data for a board type
 * @param {string} boardType - 'arrivals' or 'departures'
 */
export function clearLastKnownGood(boardType) {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${boardType}`);
    console.log(`[LastKnownGood] Cleared ${boardType} cache`);
  } catch (err) {
    // Ignore
  }
}

/**
 * Format timestamp for display
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Formatted time string
 */
function formatTimestamp(timestamp) {
  try {
    const d = new Date(timestamp);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Lagos'
    });
  } catch (err) {
    return '--:--';
  }
}

/**
 * Get the age of cached data in human-readable format
 * @param {string} boardType - 'arrivals' or 'departures'
 * @returns {string} Human readable age (e.g., "5 minutes ago")
 */
export function getCacheAge(boardType) {
  const data = getLastKnownGood(boardType);
  if (!data) return 'No cached data';

  const seconds = data.ageSeconds;
  if (seconds < 60) return 'Just now';
  if (seconds < 120) return '1 minute ago';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  return `${Math.floor(seconds / 3600)} hours ago`;
}
